import {
	Badge,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Skeleton,
} from "@ancatag/at-design";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import {
	getTokenSavings,
	type TokenSavingsResponse,
} from "../services/analytics";
import { type Project, projectService } from "../services/projects";

export default function AnalyticsTokens() {
	const [projects, setProjects] = useState<Project[]>([]);
	const [projectId, setProjectId] = useState<string | null>(null);
	const [data, setData] = useState<TokenSavingsResponse | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		projectService
			.list()
			.then((list) => {
				setProjects(list);
				if (list.length && !projectId) setProjectId(list[0].id);
			})
			.catch(() => setError("Failed to load projects"));
	}, [projectId]);

	useEffect(() => {
		if (!projectId) return;
		setLoading(true);
		setError(null);
		getTokenSavings(projectId)
			.then(setData)
			.catch(() => setError("Failed to load token savings"))
			.finally(() => setLoading(false));
	}, [projectId]);

	const cards = useMemo(() => {
		const summary = data?.summary;
		if (!summary) return [];
		const monetaryKnown =
			summary.costWithoutCache !== null &&
			summary.costActual !== null &&
			summary.costSaved !== null;
		return [
			{
				label: "Tokens Saved",
				value: summary.tokensSaved.toLocaleString(),
				sub: `${summary.savingsPercentage}% of would-be usage`,
			},
			{
				label: "Cache Hit Rate",
				value: `${summary.cacheHitRate}%`,
				sub: `Hot ${summary.hotHits} • Semantic ${summary.semanticHits}`,
			},
			{
				label: "Requests",
				value: summary.totalRequests.toLocaleString(),
				sub: `${summary.cacheHits} cached • ${summary.tokensTotal.toLocaleString()} tokens used`,
			},
			{
				label: "Cost Saved",
				value: monetaryKnown
					? `$${summary.costSaved?.toFixed(4)}`
					: "Price unknown",
				sub: monetaryKnown
					? `Would-be $${summary.costWithoutCache?.toFixed(4)} vs $${summary.costActual?.toFixed(4)}`
					: "Set pricing on model/provider to enable cost view",
			},
		];
	}, [data]);

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between flex-wrap gap-4">
				<div>
					<h1 className="text-2xl font-semibold tracking-tight">
						Token Savings
					</h1>
					<p className="text-sm text-muted-foreground">
						Cache impact, tokens saved, and latency. Monetary fields show only
						when pricing is set.
					</p>
				</div>
				<div className="w-64">
					<Select value={projectId ?? ""} onValueChange={setProjectId}>
						<SelectTrigger>
							<SelectValue placeholder="Select project" />
						</SelectTrigger>
						<SelectContent>
							{projects.map((p) => (
								<SelectItem key={p.id} value={p.id}>
									{p.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>

			{error && (
				<Card>
					<CardContent className="text-sm text-destructive py-4">
						{error}
					</CardContent>
				</Card>
			)}

			{loading && (
				<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
					{Array.from({ length: 4 }).map((_, i) => (
						<Skeleton key={i} className="h-28 w-full" />
					))}
				</div>
			)}

			{!loading && data && (
				<>
					<div className="text-sm text-muted-foreground">
						Range: {format(new Date(data.range.from), "PP")} –{" "}
						{format(new Date(data.range.to), "PP")}
					</div>

					<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
						{cards.map((card) => (
							<Card key={card.label}>
								<CardHeader>
									<CardTitle className="text-sm font-medium text-muted-foreground">
										{card.label}
									</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="text-2xl font-semibold">{card.value}</div>
									<div className="text-xs text-muted-foreground mt-1">
										{card.sub}
									</div>
								</CardContent>
							</Card>
						))}
					</div>

					<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
						<Card>
							<CardHeader>
								<CardTitle className="text-sm font-medium text-muted-foreground">
									Latency
								</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="text-2xl font-semibold">
									{data.summary.avgResponseTimeMs !== null
										? `${Math.round(data.summary.avgResponseTimeMs)} ms`
										: "N/A"}
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader>
								<CardTitle className="text-sm font-medium text-muted-foreground">
									Cache Layers
								</CardTitle>
							</CardHeader>
							<CardContent className="flex gap-2 flex-wrap">
								<Badge variant="outline">Hot: {data.summary.hotHits}</Badge>
								<Badge variant="outline">
									Semantic: {data.summary.semanticHits}
								</Badge>
								<Badge variant="outline">
									Cache hits: {data.summary.cacheHits}
								</Badge>
							</CardContent>
						</Card>

						<Card>
							<CardHeader>
								<CardTitle className="text-sm font-medium text-muted-foreground">
									Tokens
								</CardTitle>
							</CardHeader>
							<CardContent className="space-y-1 text-sm">
								<div className="flex justify-between">
									<span className="text-muted-foreground">Used</span>
									<span>{data.summary.tokensTotal.toLocaleString()}</span>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">Would-be</span>
									<span>
										{data.summary.tokensWithoutCache.toLocaleString()}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">Saved</span>
									<span>{data.summary.tokensSaved.toLocaleString()}</span>
								</div>
							</CardContent>
						</Card>
					</div>
				</>
			)}
		</div>
	);
}
